import ko from "knockout";
import * as _ from "underscore";

export interface IFilterUtilFilter {
    filterColumn: string;
    dataType: number;
    value: KnockoutObservable<any>;
    operation: (a: any, b: any) => number;
}

const filterUtil = (function () {
    const caseInsensitiveSearch = function (value: string, stringToSearch: string) {
        const convertedValue = value.toLowerCase();
        const convertedStringToSearch = stringToSearch.toLowerCase();

        return convertedValue.indexOf(convertedStringToSearch) != -1;
    };

    const operations = (function () {
        return {
            less: function (a: any, b: any) {
                return a < b;
            },
            greater: function (a: any, b: any) {
                return a > b;
            },
            lessOrEquals: function (a: any, b: any) {
                return a <= b;
            },
            greaterOrEquals: function (a: any, b: any) {
                return a >= b;
            },
            equals: function (a: any, b: any) {
                return a === b;
            },
            contains: function (a: any, b: any) {
                return caseInsensitiveSearch(a, b);
            },
            in: function (item: any, list: any[]) {
                return _.any(list, function (listItem) {
                    return listItem === item;
                });
            },
            multiColumnContains: function (list: any[], item: any) {
                return _.any(list, function (listItem) {
                    return caseInsensitiveSearch(listItem, item);
                });
            }
        };
    })();

    const dataTypes = (function () {
        return {
            date: 0,
            string: 1,
            list: 2,
            bool: 3
        };
    })();

    function filterIsNull(dataType: number, filterValue: any) {
        // Returns true if filter value is null or empty value; false otherwise.
        // For booleans false is also a non-null value, so returns false in that case.

        if (dataType === dataTypes.bool && filterValue === false) {
            return false;
        }

        if (filterValue) {
            return filterValue.toString().match(/^ *$/) !== null;
        } else {
            return true;
        }
    }

    function getFilteredList(listToFilter: any[], filters: IFilterUtilFilter[]) {
        /// listToFilter: The list to be filtered
        /// filters: Should be an array of objects, where each object must have following properties
        /// filterColumn: The column in the list item on which filtering is performed e.g., "createdDate"
        /// value:        observable that points to the value of filter e.g., "2015-04-01"
        /// dataType:     One of the available datatypes e.g., string, date.
        /// operation:    One of the available operations e.g., less, greater, lessOrEquals, in etc.
        ///               In case filter requires a complex condition which cannot be represented with one of these operations,
        ///               then a custom function can be passed instead.

        // Create a clone of the original array.
        let filteredList = listToFilter.slice(0);

        _.each(filters, function (filter) {
            let parser = function (value: any) {
                return value;
            };

            switch (filter.dataType) {
                case dataTypes.date:
                    // It is ok (looked at source) to call ko.validation.locale() to get current locale even if typings don't allow.
                    if ((ko.validation as any).locale().toLowerCase() === "nb-no") {
                        parser = function (value) {
                            return Date.parse(value.split(".").reverse().join("-"));
                        };
                        break;
                    }

                    parser = function (value) {
                        return Date.parse(value);
                    };
                    break;
                case dataTypes.string:
                    parser = function (value) {
                        if (value) return value.toString();
                        else return value;
                    };
                    break;
                case dataTypes.bool:
                    parser = function (value) {
                        if (typeof value === "string") {
                            return value === "true";
                        }

                        return value;
                    };
                    break;
            }

            if (filterIsNull(filter.dataType, filter.value())) {
                // Filter doesn't have a value, continue with the next iteration.
                return true;
            }

            // Filter has some value, filter the list further.
            filteredList = _.filter(filteredList, function (listElement) {
                let leftOperand;
                if (_.isArray(filter.filterColumn)) {
                    leftOperand = _.map(filter.filterColumn, function (column) {
                        return parser(listElement[column]);
                    });
                } else {
                    leftOperand = parser(listElement[filter.filterColumn]);
                }

                const rightOperand = parser(filter.value());

                return filter.operation(leftOperand, rightOperand);
            });

            return true;
        });

        return filteredList;
    }

    return {
        getFilteredList: getFilteredList,
        operations: operations,
        dataTypes: dataTypes
    };
})();

export default filterUtil;
